import { useCallback, useRef, useState } from "react";
import { PaperPlaneTilt, Robot, Sparkle, X } from "@phosphor-icons/react";
import { chatLayoutAssistant } from "../../api/folderSchemas";

/**
 * FolderLayoutAssistant — standalone AI chat panel for designing folder layouts.
 * Lives only on the Folder Layouts settings tab. Not connected to other agents.
 *
 * When the AI generates a layout, it calls onLayoutGenerated(layout) so the
 * parent can open the editor pre-filled with the generated structure.
 */
export default function FolderLayoutAssistant({ open, onClose, onLayoutGenerated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    const userMsg = { id: Date.now(), role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    scrollToBottom();

    setLoading(true);
    try {
      // Build history from existing messages (excluding the one we just added)
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const result = await chatLayoutAssistant(text, history);

      const assistantMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: result.reply,
        layout: result.layout || null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      scrollToBottom();

      // If a layout was generated, notify parent
      if (result.layout) {
        // Small delay so user can see the message first
        setTimeout(() => onLayoutGenerated?.(result.layout), 600);
      }
    } catch (e) {
      const errMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: "Sorry, I couldn't process that. Please try again.",
        isError: true,
      };
      setMessages((prev) => [...prev, errMsg]);
      scrollToBottom();
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, onLayoutGenerated]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex flex-col w-[400px] max-w-full border-l border-slate-200 bg-white shadow-xl"
      data-testid="folder-layout-assistant"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-violet-600 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Robot size={16} className="text-white" />
          <span className="text-sm font-semibold text-white">Layout Design Assistant</span>
        </div>
        <button
          onClick={onClose}
          className="text-violet-200 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
          data-testid="layout-assistant-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8 gap-3">
            <div className="w-12 h-12 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center">
              <Sparkle size={22} className="text-violet-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Layout Design Assistant</p>
              <p className="text-xs text-slate-400 leading-relaxed max-w-[260px]">
                Tell me about your projects and I'll design a folder structure for you.
              </p>
            </div>
            <div className="mt-2 flex flex-col gap-1.5 w-full">
              {[
                "I manage residential construction projects",
                "I need a layout for civil engineering works",
                "We do commercial fitouts with multiple contractors",
                "I want monthly folders for maintenance contracts",
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInput(suggestion)}
                  className="text-xs text-left px-3 py-2 bg-slate-50 hover:bg-violet-50 border border-slate-200 hover:border-violet-200 rounded text-slate-600 hover:text-violet-700 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
          >
            {msg.role === "assistant" && (
              <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Robot size={12} className="text-violet-600" />
              </div>
            )}
            <div className={`flex flex-col gap-1.5 max-w-[85%] ${msg.role === "user" ? "items-end" : "items-start"}`}>
              <div
                className={`px-3 py-2 rounded-lg text-xs leading-relaxed ${
                  msg.role === "user"
                    ? "bg-violet-600 text-white"
                    : msg.isError
                    ? "bg-red-50 border border-red-200 text-red-700"
                    : "bg-slate-100 text-slate-800"
                }`}
              >
                {msg.content.split("\n").map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < msg.content.split("\n").length - 1 && <br />}
                  </span>
                ))}
              </div>
              {msg.layout && (
                <div className="flex items-center gap-1.5 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] font-semibold text-emerald-700">
                  <Sparkle size={11} weight="fill" />
                  Layout generated — opening editor…
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 py-1">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Robot size={12} className="text-violet-500" />
            </div>
            <div className="flex gap-1 items-center">
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <span className="text-[10px] text-slate-500">Designing your layout…</span>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-slate-100 bg-slate-50">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your project type…"
            rows={2}
            disabled={loading}
            className="flex-1 resize-none text-sm px-3 py-2 border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 placeholder-slate-400 disabled:opacity-50"
            data-testid="layout-assistant-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded transition-colors"
            data-testid="layout-assistant-send"
          >
            <PaperPlaneTilt size={15} />
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
}
